import { Platform } from 'react-native';
import ProductionStorage from '../utils/productionStorage';
import { VehicleProfile } from '../types/vehicle';

type VehicleInput = Omit<VehicleProfile, 'id' | 'createdAt' | 'updatedAt'> & { id?: string };

class VehicleGarageService {
  private storageKey(userId: string) {
    return `vehicles_${userId}`;
  }

  private sortVehicles(vehicles: VehicleProfile[]): VehicleProfile[] {
    return [...vehicles].sort((a, b) => {
      const ad = a.isDefault ? 1 : 0;
      const bd = b.isDefault ? 1 : 0;
      if (ad !== bd) return bd - ad;
      return (b.updatedAt || '').localeCompare(a.updatedAt || '');
    });
  }

  private async saveLocal(userId: string, vehicles: VehicleProfile[]): Promise<void> {
    await ProductionStorage.setItem(this.storageKey(userId), JSON.stringify(vehicles));
  }

  private async loadLocal(userId: string): Promise<VehicleProfile[]> {
    const json = await ProductionStorage.getItem(this.storageKey(userId));
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? (parsed as VehicleProfile[]) : [];
    } catch {
      return [];
    }
  }

  async listVehicles(userId: string): Promise<VehicleProfile[]> {
    // Firestore first, fallback to local
    try {
      const loaded = await this.loadFromFirestore(userId);
      if (loaded) {
        const sorted = this.sortVehicles(loaded);
        await this.saveLocal(userId, sorted);
        return sorted;
      }
    } catch (_) {
      // ignore, fallback
    }
    return this.sortVehicles(await this.loadLocal(userId));
  }

  async upsertVehicle(userId: string, input: VehicleInput): Promise<VehicleProfile> {
    const now = new Date().toISOString();
    const id = input.id || `veh_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const vehicle: VehicleProfile = {
      id,
      userId,
      nickname: input.nickname,
      year: input.year,
      make: input.make,
      model: input.model,
      trim: input.trim,
      vin: input.vin,
      plate: input.plate,
      notes: input.notes,
      isDefault: input.isDefault,
      createdAt: (input as any).createdAt || now,
      updatedAt: now,
    };

    const existing = await this.listVehicles(userId);
    let next = existing.filter(v => v.id !== id).concat(vehicle);

    // If setting default, clear others
    if (vehicle.isDefault) {
      next = next.map(v => (v.id === id ? { ...v, isDefault: true } : { ...v, isDefault: false }));
    } else {
      // Ensure we keep at least one default if none exists
      if (!next.some(v => v.isDefault)) {
        const newest = this.sortVehicles(next)[0];
        if (newest) {
          next = next.map(v => (v.id === newest.id ? { ...v, isDefault: true } : v));
        }
      }
    }

    next = this.sortVehicles(next);
    await this.saveLocal(userId, next);

    // Best-effort Firestore sync
    try {
      await this.saveToFirestore(userId, vehicle);
      // Also update default flags if needed (when we changed others)
      if (vehicle.isDefault) {
        await this.syncDefaultFlagsToFirestore(userId, next);
      }
    } catch (e) {
      console.warn('⚠️ vehicleGarageService: Firestore sync failed (upsert):', (e as any)?.message);
    }

    return next.find(v => v.id === id)!;
  }

  async deleteVehicle(userId: string, vehicleId: string): Promise<void> {
    const existing = await this.listVehicles(userId);
    let next = existing.filter(v => v.id !== vehicleId);

    // If we deleted default, choose a new default if any left
    if (existing.some(v => v.id === vehicleId && v.isDefault) && next.length > 0) {
      const newest = this.sortVehicles(next)[0];
      next = next.map(v => (v.id === newest.id ? { ...v, isDefault: true } : { ...v, isDefault: false }));
    }

    next = this.sortVehicles(next);
    await this.saveLocal(userId, next);

    try {
      await this.deleteFromFirestore(userId, vehicleId);
      await this.syncDefaultFlagsToFirestore(userId, next);
    } catch (e) {
      console.warn('⚠️ vehicleGarageService: Firestore sync failed (delete):', (e as any)?.message);
    }
  }

  async setDefaultVehicle(userId: string, vehicleId: string): Promise<void> {
    const existing = await this.listVehicles(userId);
    const next = this.sortVehicles(existing.map(v => ({ ...v, isDefault: v.id === vehicleId })));
    await this.saveLocal(userId, next);
    try {
      await this.syncDefaultFlagsToFirestore(userId, next);
    } catch (e) {
      console.warn('⚠️ vehicleGarageService: Firestore sync failed (setDefault):', (e as any)?.message);
    }
  }

  async getDefaultVehicle(userId: string): Promise<VehicleProfile | null> {
    const vehicles = await this.listVehicles(userId);
    return vehicles.find(v => v.isDefault) || vehicles[0] || null;
  }

  private async loadFromFirestore(userId: string): Promise<VehicleProfile[] | null> {
    if (!userId) return null;

    if (Platform.OS === 'web') {
      const { collection, getDocs } = require('firebase/firestore');
      const { initializeFirebase, getWebDb } = require('./unifiedAuthService');
      if (!initializeFirebase()) return null;
      const db = getWebDb();
      if (!db) return null;

      const colRef = collection(db, 'users', userId, 'vehicles');
      const snap = await getDocs(colRef);
      const vehicles = snap.docs.map((d: any) => ({ id: d.id, ...(d.data() || {}), userId } as VehicleProfile));
      return vehicles;
    }

    // Mobile (RN Firebase)
    try {
      const { initializeFirebase } = require('./unifiedAuthService');
      if (!initializeFirebase()) return null;
      const { getApp } = require('@react-native-firebase/app');
      const { getFirestore, collection, getDocs } = require('@react-native-firebase/firestore');
      const app = getApp();
      const db = getFirestore(app);
      const colRef = collection(db, 'users', userId, 'vehicles');
      const snap = await getDocs(colRef);
      const vehicles = snap.docs.map((d: any) => ({ id: d.id, ...(d.data() || {}), userId } as VehicleProfile));
      return vehicles;
    } catch (e) {
      return null;
    }
  }

  private async saveToFirestore(userId: string, vehicle: VehicleProfile): Promise<void> {
    if (!userId) return;
    const payload = {
      ...vehicle,
      userId,
      updatedAt: new Date().toISOString(),
    };

    if (Platform.OS === 'web') {
      const { doc, setDoc } = require('firebase/firestore');
      const { initializeFirebase, getWebDb } = require('./unifiedAuthService');
      if (!initializeFirebase()) return;
      const db = getWebDb();
      if (!db) return;
      await setDoc(doc(db, 'users', userId, 'vehicles', vehicle.id), payload, { merge: true });
      return;
    }

    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, setDoc } = require('@react-native-firebase/firestore');
    const app = getApp();
    const db = getFirestore(app);
    await setDoc(doc(db, 'users', userId, 'vehicles', vehicle.id), payload, { merge: true });
  }

  private async deleteFromFirestore(userId: string, vehicleId: string): Promise<void> {
    if (!userId) return;
    if (Platform.OS === 'web') {
      const { doc, deleteDoc } = require('firebase/firestore');
      const { initializeFirebase, getWebDb } = require('./unifiedAuthService');
      if (!initializeFirebase()) return;
      const db = getWebDb();
      if (!db) return;
      await deleteDoc(doc(db, 'users', userId, 'vehicles', vehicleId));
      return;
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, deleteDoc } = require('@react-native-firebase/firestore');
    const app = getApp();
    const db = getFirestore(app);
    await deleteDoc(doc(db, 'users', userId, 'vehicles', vehicleId));
  }

  private async syncDefaultFlagsToFirestore(userId: string, vehicles: VehicleProfile[]): Promise<void> {
    if (!userId) return;
    const defaultId = vehicles.find(v => v.isDefault)?.id || null;
    if (!defaultId) return;

    // Best-effort: write isDefault flags for all vehicles we know about
    await Promise.all(vehicles.map(async v => {
      try {
        await this.saveToFirestore(userId, { ...v, isDefault: v.id === defaultId });
      } catch (_) {
        // ignore per-doc failures
      }
    }));
  }
}

export const vehicleGarageService = new VehicleGarageService();
export default vehicleGarageService;

